import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import {
  IconUpload,
  IconInfoCircle,
  IconAdjustments,
  IconAlertTriangle,
  IconCheck,
  IconFileText,
} from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Button } from '@/components/buttons/Button'
import Select from '@/components/forms/Select'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { Card } from '@/components/cards/Card'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { DragAndDropFileUploader } from '@/components/forms/formik/file-uploader/DragAndDropFileUploader'
import { toast } from '@/components/toasts/toast.helpers'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Banner } from '@/components/banners/Banner'
import { FormField } from '@/components/forms/FormField'
import { apiService } from '@/api'

export const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

const validationSchema = Yup.object().shape({
  organization: Yup.number().required(),
  files: Yup.array().min(1).required(),
})

const buildKnowledgeParams = (configCids = []) => {
  const params = new URLSearchParams({
    doc_type: 'customer_document',
  })

  if (!isEmpty(configCids)) {
    configCids.forEach((configCid) => params.append('config_cids', configCid))
  }

  return params
}

const getInitialValues = (canSelectOrganization, currentUserOrgId) => ({
  organization: canSelectOrganization ? null : currentUserOrgId,
  config_cids: [],
  files: [],
})

const buildAuthHeaders = (token, username, organizationId) => ({
  token,
  username,
  organizationid: organizationId,
})

const fetchKnowledgeFiles = (orgId, params, options) =>
  fetch(`${apiService.agentGpt}/files/${orgId}?${params.toString()}`, options)

const PlaybookLabel = ({ label, tooltip }) => (
  <label className="flex-align-center small-gap">
    {label}
    <InteractiveTooltip content={tooltip}>
      <IconInfoCircle className="icon-svg muted-text" />
    </InteractiveTooltip>
  </label>
)

// The message goes in as children, not `content`. With an icon, Banner lays out
// as a grid whose `div { grid-column: 2 }` rule only catches elements — a string
// passed as `content` renders as a bare text node, auto-places into the `auto`
// first column and sizes it to the text, squeezing the header into a sliver.
// As children, Banner wraps it in `.banner__content` and it lands in column 2.
export const CriticalBanner = ({ header, message }) => (
  <Banner critical header={header} icon={<IconAlertTriangle size={18} />}>
    {message}
  </Banner>
)

export const InfoBanner = ({ header, message }) => (
  <Banner informative header={header} icon={<IconInfoCircle size={18} />}>
    {message}
  </Banner>
)

export const KnowledgeCard = ({
  title,
  subtitle,
  icon: IconComponent,
  iconClassName,
  testId,
  children,
}) => (
  <Card
    title={title}
    subtitle={subtitle}
    icon={
      <div className={`interaction-icon-container ${iconClassName}`}>
        <IconComponent size={24} />
      </div>
    }
    data-testid={testId}
  >
    {children}
  </Card>
)

// Both knowledge base uploaders (an organization's and Balto's) take the same files; agent-gpt
// reads them the same way. Only where they upload differs.
const KNOWLEDGE_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'text/plain': ['.txt', '.csv'],
  'text/markdown': ['.md'],
  'application/csv': ['.csv'],
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
  'application/octet-stream': ['.docx', '.zip', '.csv'],
}

export const KnowledgeDocumentsUploader = (props) => (
  <DragAndDropFileUploader
    name="files"
    label="Documents"
    multiple
    maxFiles={50}
    maxSize={MAX_FILE_SIZE}
    acceptedFileTypes={KNOWLEDGE_FILE_TYPES}
    acceptedFileMessage={
      <>
        <span>Supported formats: PDF, DOCX, TXT, MD, CSV, ZIP </span>
        <span>Max file size: 20MB</span>
      </>
    }
    useIconLayout={false}
    {...props}
  />
)

export const handleApiError = (context, message, setError, err) => {
  console.error(context, err)
  if (setError) {
    setError(message)
  }
  toast.error(message)
}

export const UploadKnowledge = ({
  canSelectOrganization,
  currentUserOrgId,
  token,
  username,
  organizationOptions,
  playbooks = [],
  playbooksLoading = false,
  onOrganizationSelect,
  onBack,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [uploadError, setUploadError] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const uploadSuccess = uploadedFiles.length > 0

  const authHeaders = buildAuthHeaders(token, username, currentUserOrgId)
  const initialValues = getInitialValues(canSelectOrganization, currentUserOrgId)

  const handleSubmit = async (values, { setSubmitting, setFieldValue }) => {
    setIsUploading(true)
    setUploadedFiles([])
    setUploadError(null)

    let uploaded = []

    try {
      for (const file of values.files) {
        const formData = new FormData()
        formData.append('file', file)

        const params = buildKnowledgeParams(values.config_cids)

        const res = await fetchKnowledgeFiles(values.organization, params, {
          method: 'POST',
          headers: authHeaders,
          body: formData,
        })

        const data = await res.json()

        if (!res.ok) {
          throw data
        }

        if (Array.isArray(data?.uploaded_files)) {
          uploaded = uploaded.concat(data.uploaded_files)
        }
      }

      setUploadedFiles(uploaded)
      setFieldValue('files', [])
      toast.success(
        `Uploaded ${uploaded.length} document${uploaded.length === 1 ? '' : 's'} successfully`
      )
    } catch (err) {
      handleApiError(
        'Upload failed:',
        'There was a problem while uploading your document(s). Please try again.',
        setUploadError,
        err
      )
    } finally {
      setSubmitting(false)
      setIsUploading(false)
    }
  }

  const handleBack = (resetForm) => {
    if (resetForm) {
      resetForm()
    }
    setUploadedFiles([])
    setUploadError(null)
    setIsUploading(false)
    onBack()
  }

  return (
    <KnowledgeCard
      title="Upload Knowledge"
      subtitle="Upload documents to AgentGPT"
      icon={IconUpload}
      iconClassName="ai-answers-upload"
      testId="ai-answers-upload-card"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({
          values,
          errors,
          isSubmitting,
          submitForm,
          setFieldValue,
          resetForm,
          setSubmitting,
        }) => {
          const isUploadBusy = isSubmitting || isUploading
          const isUploadLocked = isUploadBusy || uploadSuccess
          const organizationSelectProps = {
            options: organizationOptions,
            value: values.organization,
            placeholder: 'Select Organization',
            isClearable: false,
            fixedWidth: true,
            onChange: (event) => {
              setFieldValue('organization', event.value)
              setFieldValue('config_cids', [])
              onOrganizationSelect(event.value)
            },
            isDisabled: isUploadLocked,
          }
          const playbookSelectProps = {
            placeholder: 'Select Playbooks',
            loading: playbooksLoading,
            options: playbooks,
            value: playbooks.filter((option) => values.config_cids?.includes(option.value)),
            onChange: (selected) =>
              setFieldValue('config_cids', selected?.map((option) => option.value) || []),
            fixedWidth: true,
            disabled: isUploadLocked || !values.organization,
          }

          return (
            <div className="field-stack">
              <div className="flex-align-center flex-wrap gap">
                {canSelectOrganization && (
                  <FormField className="half-width" data-testid="ai-answers-upload-organization">
                    <label>Organization</label>
                    <Select {...organizationSelectProps} />
                  </FormField>
                )}

                <FormField className="half-width">
                  <PlaybookLabel
                    label="Playbooks (optional)"
                    tooltip="Leaving playbooks unselected will upload the document(s) globally"
                  />
                  <MultiSelect {...playbookSelectProps} />
                </FormField>
              </div>

              <hr className="mb" />

              <KnowledgeDocumentsUploader
                allowEdits={!isUploadBusy}
                values={values}
                setFieldValue={(field, value) => {
                  if (field === 'files') {
                    setUploadedFiles([])
                    setUploadError(null)
                  }

                  setFieldValue(field, value)
                }}
                setSubmitting={setSubmitting}
              />

              {uploadSuccess && (
                <Banner success header="Upload complete" icon={<IconCheck size={18} />}>
                  <p className="mbs bold">
                    {uploadedFiles.length} document
                    {uploadedFiles.length > 1 ? 's were' : ' was'} uploaded successfully:
                  </p>
                  <ul className="ai-answers-list">
                    {uploadedFiles.map((file) => (
                      <li key={file} className="ai-answers-list__item">
                        <IconFileText size={16} className="ai-answers-list__icon" />
                        <span className="ai-answers-list__name">{file}</span>
                      </li>
                    ))}
                  </ul>
                </Banner>
              )}

              {uploadError && !uploadSuccess && (
                <CriticalBanner header="Upload failed" message={uploadError} />
              )}

              <div className="flex-align-center flex-space-between mt">
                <div>
                  {!uploadSuccess && !isUploadBusy && !isEmpty(values.files) && (
                    <p className="muted-text small">
                      {' '}
                      Please review selections above before continuing
                    </p>
                  )}
                </div>

                <div>
                  <Button
                    type="button"
                    className="mr"
                    data-testid="ai-answers-upload-back"
                    onClick={() => handleBack(resetForm)}
                    disabled={isUploadBusy}
                  >
                    Back
                  </Button>
                  <Button
                    primary
                    type="button"
                    data-testid="ai-answers-upload-submit"
                    onClick={submitForm}
                    disabled={
                      uploadSuccess || isUploadBusy || !isEmpty(errors) || isEmpty(values.files)
                    }
                  >
                    {isUploadBusy ? 'Uploading...' : 'Start Upload'}
                  </Button>
                </div>
              </div>
            </div>
          )
        }}
      </Formik>
    </KnowledgeCard>
  )
}

export const ManageKnowledge = ({
  canSelectOrganization,
  currentUserOrgId,
  token,
  username,
  organizationOptions,
  playbooks = [],
  playbooksLoading = false,
  onOrganizationSelect,
  onBack,
}) => {
  const [knowledgeList, setKnowledgeList] = useState([])
  const [knowledgeLoading, setKnowledgeLoading] = useState(false)
  const [knowledgeInitialized, setKnowledgeInitialized] = useState(false)
  const [knowledgeError, setKnowledgeError] = useState(null)
  const [manageOrgId, setManageOrgId] = useState(canSelectOrganization ? null : currentUserOrgId)
  const [manageConfigCid, setManageConfigCid] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [deletingFileName, setDeletingFileName] = useState(null)

  useEffect(() => {
    if (!canSelectOrganization) {
      setManageOrgId(currentUserOrgId)
    }
  }, [canSelectOrganization, currentUserOrgId])

  const authHeaders = buildAuthHeaders(token, username, currentUserOrgId)

  const fetchKnowledge = async (orgId, configCid) => {
    if (!orgId) {
      return
    }

    setKnowledgeLoading(true)
    setKnowledgeError(null)

    try {
      const params = buildKnowledgeParams(configCid ? [configCid] : [])

      const res = await fetchKnowledgeFiles(orgId, params, {
        method: 'GET',
        headers: authHeaders,
      })

      const data = await res.json()

      if (!res.ok) {
        throw data
      }

      const files = Array.isArray(data?.files) ? data.files : data
      setKnowledgeList(Array.isArray(files) ? files : [])
    } catch (err) {
      handleApiError(
        'Fetch knowledge failed:',
        'Unable to load documents. Please try again.',
        setKnowledgeError,
        err
      )
    } finally {
      setKnowledgeLoading(false)
      setKnowledgeInitialized(true)
    }
  }

  const handleManageOrgChange = (orgId) => {
    setManageOrgId(orgId)
    setManageConfigCid(null)

    if (orgId) {
      onOrganizationSelect(orgId)
    }
  }

  const deleteKnowledgeFile = async (orgId, fileName) => {
    if (!orgId || !fileName) {
      return
    }

    setDeleteLoading(true)
    setDeleteError(null)
    setDeletingFileName(fileName)

    try {
      const params = new URLSearchParams({
        file_path: fileName,
      })

      const res = await fetchKnowledgeFiles(orgId, params, {
        method: 'DELETE',
        headers: authHeaders,
      })

      let data = null
      try {
        data = await res.json()
      } catch (err) {
        data = null
      }

      if (!res.ok) {
        throw data
      }

      toast.success(`Deleted ${fileName}`)
      setKnowledgeList((prev) => prev.filter((file) => file !== fileName))
    } catch (err) {
      handleApiError(
        'Delete failed:',
        'Unable to delete document. Please try again.',
        setDeleteError,
        err
      )
    } finally {
      setDeleteLoading(false)
      setDeletingFileName(null)
    }
  }

  const renderPlaybookSelect = (disabled) => (
    <FormField className="half-width">
      <PlaybookLabel
        label="Playbook (optional)"
        tooltip="Leaving playbooks unselected will include documents uploaded globally"
      />
      <Select
        placeholder="Select a Playbook"
        loading={playbooksLoading}
        options={playbooks}
        value={manageConfigCid}
        onChange={(event) => setManageConfigCid(event?.value || null)}
        isClearable
        fixedWidth
        isDisabled={disabled}
      />
    </FormField>
  )

  const rows = knowledgeList.map((fileName) => ({ id: fileName, fileName }))
  const columns = [
    {
      accessor: 'fileName',
      label: 'Document',
      isSearchable: true,
      isCopiable: true,
    },
    {
      accessor: 'actions',
      label: '',
      collapsing: true,
      format: (_, row) => (
        <Button
          size="tiny"
          critical
          type="button"
          onClick={() => {
            deleteKnowledgeFile(manageOrgId || currentUserOrgId, row.fileName)
          }}
          disabled={deleteLoading}
          loading={deleteLoading && deletingFileName === row.fileName}
        >
          Delete
        </Button>
      ),
    },
  ]

  const isBusy = knowledgeLoading || deleteLoading
  const playbookSelect = renderPlaybookSelect(isBusy || (canSelectOrganization && !manageOrgId))
  const loadDisabled = isBusy || (!manageOrgId && canSelectOrganization)

  return (
    <KnowledgeCard
      title="Manage Knowledge"
      subtitle="Review and remove documents available to AgentGPT"
      icon={IconAdjustments}
      iconClassName="ai-answers-search-or-delete"
      testId="ai-answers-manage-card"
    >
      {canSelectOrganization ? (
        <div className="flex-align-center flex-wrap gap">
          <FormField className="half-width">
            <label>Organization</label>
            <Select
              options={organizationOptions}
              value={manageOrgId}
              placeholder="Select Organization"
              isClearable={false}
              fixedWidth
              onChange={(event) => {
                handleManageOrgChange(event.value)
              }}
              isDisabled={isBusy}
            />
          </FormField>

          {playbookSelect}
        </div>
      ) : (
        playbookSelect
      )}

      {knowledgeError && (
        <CriticalBanner header="Unable to load knowledge" message={knowledgeError} />
      )}

      {deleteError && <CriticalBanner header="Delete failed" message={deleteError} />}

      {knowledgeLoading ? (
        <div
          className="muted-text mt full-width flex-align-center flex-center small-gap"
          role="status"
          aria-live="polite"
          aria-label="Loading documents"
        >
          <Loader size="small" />
        </div>
      ) : knowledgeList.length > 0 ? (
        <>
          <p className="muted-text mt">Currently uploaded documents ({knowledgeList.length})</p>
          <Card className="no-padding">
            <AdvancedTable rows={rows} columns={columns} pagination rowsPerPage={10} />
          </Card>
        </>
      ) : knowledgeInitialized ? (
        <InfoBanner
          header="No documents found"
          message="Uploaded knowledge will appear here once documents are processed."
        />
      ) : null}

      <div className="flex-align-center flex-space-between mt">
        <div />
        <div>
          <Button
            type="button"
            className="mr"
            data-testid="ai-answers-manage-back"
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            type="button"
            className="mr"
            data-testid="ai-answers-manage-load"
            onClick={() => fetchKnowledge(manageOrgId || currentUserOrgId, manageConfigCid || null)}
            disabled={loadDisabled}
          >
            Load Documents
          </Button>
        </div>
      </div>
    </KnowledgeCard>
  )
}
